import { useEffect, useId, useRef, useState } from 'react';
import { Search, X, UserRound, CalendarDays, ArrowUpRight } from 'lucide-react';
import { clientsAPI, appointmentsAPI } from '../lib/api';
import SearchSuggestions, { patientContact } from './SearchSuggestions';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
const rows = response => Array.isArray(response?.data) ? response.data : (response?.data?.items || []);
function appointmentLabel(row) {
  const name = row.client_details_visible === false ? '' : row.client_name;
  return [name, row.service_name || 'Termin'].filter(Boolean).join(' · ');
}
function appointmentDate(value) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? dateFormat.format(date) : 'Datum nicht verfügbar';
}

// The parent keys this component by identity/location. No patient search history
// is persisted. Existing backend endpoints remain responsible for authorization.
export default function GlobalSearch({ navigation, canReadClients, canReadCalendar, personLabel, onNavigate, onActiveChange }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [shortcutKey] = useState(() => typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgentData?.platform || navigator.platform || '') ? '⌘' : 'Strg');
  const [result, setResult] = useState({ query: '', options: [], loading: false, failures: [] });
  const input = useRef(null);
  const wrapper = useRef(null);
  const trigger = useRef(null);
  const id = useId();
  const term = query.trim();
  const pages = [];
  const seen = new Set();
  for (const page of navigation.flatMap(item => [item, ...(item.children || [])])) {
    if (page.href && !seen.has(page.href) && term && page.name.toLocaleLowerCase('de').includes(term.toLocaleLowerCase('de'))) {
      seen.add(page.href);
      pages.push({ id: `page:${page.href}`, title: page.name, detail: 'Bereich öffnen', kind: 'page', href: page.href });
    }
  }
  const pageOptions = pages.slice(0, 4);
  const active = expanded || focused;
  useEffect(() => { onActiveChange(active); return () => onActiveChange(false); }, [active, onActiveChange]);
  useEffect(() => {
    const shortcut = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !event.altKey) {
        // Do not steal focus from a modal with its own focus trap.
        if (document.querySelector('[role="dialog"]')) return;
        event.preventDefault();
        setExpanded(true);
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, []);
  useEffect(() => { if (expanded) input.current?.focus(); }, [expanded]);
  useEffect(() => {
    if (!expanded) return undefined;
    const dismiss = event => { if (!wrapper.current?.contains(event.target)) setExpanded(false); };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [expanded]);

  useEffect(() => {
    if (!term || !active) return undefined;
    const controller = new AbortController();
    let current = true;
    setResult({ query: term, options: [], loading: true, failures: [] });
    const timer = window.setTimeout(async () => {
      // A bounded lookup, no background polling or unbounded pagination.
      const jobs = [];
      if (canReadClients) jobs.push({ kind: 'client', run: () => clientsAPI.list({ search: term, limit: 5, lookup: true, location_id: 'all' }, { signal: controller.signal }) });
      if (canReadCalendar && term.length >= 2) jobs.push({ kind: 'appointment', run: () => appointmentsAPI.search({ q: term, limit: 5, mode: 'full', scan_limit: 50 }, { signal: controller.signal }) });
      const responses = await Promise.allSettled(jobs.map(job => job.run()));
      if (!current) return;
      const options = [], failures = [];
      responses.forEach((response, index) => {
        const kind = jobs[index].kind;
        if (response.status !== 'fulfilled') { failures.push(kind); return; }
        rows(response.value).slice(0, 5).forEach(row => {
          if (!row.id) return;
          options.push(kind === 'client' ? {
            id: `client:${row.id}`, kind, title: [row.first_name, row.last_name].filter(Boolean).join(' ') || personLabel,
            detail: patientContact(row), href: `/clients/${encodeURIComponent(row.id)}`,
          } : {
            id: `appointment:${row.id}`, kind, title: appointmentLabel(row),
            detail: [appointmentDate(row.start_time), row.staff_name].filter(Boolean).join(' · '),
            href: `/calendar?appointment_id=${encodeURIComponent(row.id)}&open=1`,
          });
        });
      });
      setResult({ query: term, options, loading: false, failures });
    }, 300);
    return () => { current = false; window.clearTimeout(timer); controller.abort(); };
  }, [term, active, canReadClients, canReadCalendar, personLabel, attempt]);

  const matching = result.query === term;
  const loading = Boolean(term && active && (!matching || result.loading));
  const options = [...(matching ? result.options.filter(option => option.kind === 'client' ? canReadClients : canReadCalendar) : []), ...pageOptions];
  const failures = matching ? result.failures : [];
  const close = () => { setExpanded(false); setFocused(false); trigger.current?.focus(); };
  return <div ref={wrapper} className="shrink-0 xl:min-w-0 xl:w-full xl:max-w-md"
    onFocusCapture={() => setFocused(true)}
    onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
    onKeyDown={event => { if (event.key === 'Escape' && !event.defaultPrevented) close(); }}>
    <button ref={trigger} type="button" aria-label={expanded ? "Suche schließen" : "Suche öffnen"} aria-expanded={expanded} aria-controls={expanded ? id : undefined}
      onClick={() => setExpanded(value => !value)} className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#E5E5EA] bg-white hover:bg-[#F5F5F7] xl:hidden">
      {expanded ? <X className="h-5 w-5" aria-hidden="true" /> : <Search className="h-5 w-5" aria-hidden="true" />}
    </button>
    <div id={id} className={`${expanded ? 'block' : 'hidden xl:block'} absolute left-3 right-3 top-full mt-2 rounded-xl border border-[#E5E5EA] bg-white p-3 shadow-lg xl:relative xl:inset-auto xl:mt-0 xl:border-0 xl:bg-transparent xl:p-0 xl:shadow-none`}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-3 z-10 h-4 w-4 text-[#86868B]" aria-hidden="true" />
        <SearchSuggestions inputRef={input} type="search" maxLength={120} value={query} onChange={setQuery}
          aria-label={`${canReadClients ? `${personLabel}, ` : ''}Termine und Bereiche suchen`}
          placeholder={canReadClients ? `${personLabel}, Termine suchen …` : 'Termine, Bereiche suchen …'}
          className={`h-10 rounded-xl border-[#E5E5EA] bg-[#FAFAFB] pl-9 text-sm [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none ${query ? 'pr-11' : 'pr-3 xl:pr-20'}`}
          dropdownClassName="max-h-[min(28rem,60dvh)] xl:w-[440px] max-w-[calc(100vw-2rem)]"
          options={options} immediateOptions={pageOptions} loading={loading}
          emptyLabel="Keine Treffer. Versuche einen anderen Suchbegriff."
          onSelect={option => { if (onNavigate(option.href) !== false) { setQuery(''); setExpanded(false); input.current?.blur(); } }}
          renderOption={option => { const Icon = option.kind === 'client' ? UserRound : option.kind === 'appointment' ? CalendarDays : ArrowUpRight;
            return <span className="flex min-w-0 items-start gap-3"><Icon className="mt-1 h-4 w-4 shrink-0 text-[#86868B]" aria-hidden="true" /><span className="min-w-0"><span className="block text-[10px] uppercase tracking-wide text-[#86868B]">{option.kind === 'client' ? personLabel : option.kind === 'appointment' ? 'Termin' : 'Bereich'}</span><span className="block truncate font-medium">{option.title}</span>{option.detail && <span className="block truncate text-xs text-[#86868B]">{option.detail}</span>}</span></span>; }}
          footer={<><span>Alle zugänglichen Standorte · Bis zu 5 Treffer je Kategorie.</span>{term.length < 2 && canReadCalendar && <span className="block">Terminsuche ab zwei Zeichen.</span>}{failures.length > 0 && <span role="status" className="mt-1 block text-red-600">{failures.map(kind => kind === 'client' ? personLabel : 'Termine').join(' und ')} konnten nicht geladen werden. <button type="button" className="underline" onClick={() => setAttempt(value => value + 1)}>Erneut versuchen</button></span>}</>} />
        {query ? <button type="button" aria-label="Suche leeren" title="Suche leeren"
          onMouseDown={event => event.preventDefault()}
          onClick={() => { setQuery(''); input.current?.focus(); }}
          className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-black hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30">
          <X className="h-4 w-4" aria-hidden="true" />
        </button> : <button type="button" title={`Suche öffnen (${shortcutKey} + K)`} aria-label={`Suche öffnen mit ${shortcutKey === '⌘' ? 'Command' : 'Steuerung'} und K`}
          onClick={() => input.current?.focus()}
          className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded-md px-1 py-1 text-[11px] text-[#6E6E73] hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/30 xl:flex">
          <kbd className="rounded border border-[#E5E5EA] bg-white px-1.5 py-0.5 font-sans" aria-hidden="true">{shortcutKey}</kbd>
          <kbd className="rounded border border-[#E5E5EA] bg-white px-1.5 py-0.5 font-sans" aria-hidden="true">K</kbd>
        </button>}
      </div>
    </div>
  </div>;
}
